import React, { useState, useEffect, useCallback } from 'react';
import { MONEY } from '@shared_components/index';
import { ItemLine, RxProduct, RXValues, INITIAL_RX_VALUES, LineInput, INITIAL_RX_ITEM_LINE } from './types';
import { LineHandler } from '@src/components/RxLineItemHandler';
import { RxDiscountHandler } from '@src/components/RxDiscountHandler';
import { useCart } from '@src/components/CartProvider';
import { TooltipElement } from '@shared_components/components/Tooltip';


type FormProps = {
    product: RxProduct;
    loading: boolean;
    files: File[];
    setFiles: (files: File[]) => void;
}

export const RxSiteBody = ({ product, loading, files, setFiles }: FormProps) => {
    const { values, handleSetValues, changesMade, setValues } = useCart();
    const [prices, setPrices] = useState<{ total_price: number; discounted_price: number }>({
        total_price: 0,
        discounted_price: 0,
    });


    const handleChangeQty = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newQty = parseInt(e.target.value);
        if (isNaN(newQty)) { return }
        // set new qty, if new qty is less than current, remove item line, if more then add item line(s)
        handleSetValues({
            ...values,
            qty: newQty,
            itemLines: newQty > values.itemLines.length
                ? [...values.itemLines, ...Array.from({ length: newQty - values.itemLines.length }, () => INITIAL_RX_ITEM_LINE)]
                : values.itemLines.slice(0, newQty),
            pdErrorIndices: Object.fromEntries(
                Object.entries(values.pdErrorIndices).filter(([key]) => {
                    const lineIndex = parseInt(key.split('-')[0]);
                    return lineIndex < newQty;
                }
                )
            )
        });
        
    };

    const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const filesArray = Array.from(e.target.files);
            const validFiles = filesArray.filter(file => file.type.startsWith('image/'));
            if (validFiles.length > 0) {
                setFiles(validFiles);
            }
        } else {
            console.error("No files selected");
            e.target.value = ""; // Clear the input value
        }
    };

    // Calculate total price (example logic)
    const calculatePrices = useCallback(() => {
        const quantity = values.qty || 1;
        const baseTotal = parseFloat(product.price) * quantity;
        let discountedTotal: number | undefined = undefined;

        if (values.discountAmount > 0) { // Example discount logic
            discountedTotal =  baseTotal - (baseTotal * (values.discountAmount / 100));
        }
        setPrices({
            total_price: baseTotal,
            discounted_price: discountedTotal || baseTotal,
        });
    }, [product, values.qty, values.discountAmount, setPrices]);

    useEffect(() => {
        calculatePrices();
    }, [calculatePrices]);


    return (
        <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-2">
                    <label htmlFor="orderer_email" className="block text-sm font-medium text-gray-700 mb-1">Email:</label>
                    <input
                        type="email"
                        id="email"
                        name="email"
                        value={values.email}
                        onChange={(e) => handleSetValues({ ...values, email: e.target.value })}
                        required
                        className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                </div>
                <div>
                    <label htmlFor="qty" className="block text-sm font-medium text-gray-700 mb-1">Qty:</label>
                    <input
                        type="number"
                        min="1"
                        max="100" // Example max
                        name="qty"
                        id="qty"
                        value={values.qty}
                        onChange={(e) => {
                            handleChangeQty(e);
                        }}
                        required
                        className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                </div>
            </div>

            <div className="text-right mt-4">
                <p className="text-sm font-medium text-gray-700">Total Price:</p>
                <div className="flex justify-end items-baseline space-x-2 mt-1">
                    {values.discountAmount > 0 ? (
                        <>
                            <p className="text-lg text-gray-500 line-through">{MONEY.format(prices.total_price)}</p>
                            <p className="text-2xl font-semibold text-orange-600">{MONEY.format(prices.discounted_price)}</p>
                        </>
                    ) : (
                        <p className="text-2xl font-semibold text-gray-900">{MONEY.format(prices.total_price)}</p>
                    )}
                </div>
            </div>

            <div className="space-y-3 text-sm text-gray-600 mt-6">
                <p>
                    Please fill out the prescription information below. Complete all fields for which you have information. For the best results, the PD (Pupillary Distance) is also required. If you wish to order RX inserts for multiple people at the same time, adjust the quantity and fill out each prescription, making sure the name of the user is correctly filled in with their prescription.
                </p>
                <p>
                    Our lenses for Paintball will be single vision lenses. If your prescription has ADD for bifocals we will ignore that part of the prescription.
                </p>
                <p>
                    Estimated delivery time is 4-6 weeks. If you have any questions, please contact us at <a href="mailto:support@crbnpaintball.com" className="text-indigo-600 hover:text-indigo-800 underline">support@crbnpaintball.com</a>
                </p>
            </div>

            <div className="mt-6">
                <LineHandler
                    itemLines={values.itemLines}
                    pdErrorIndices={values.pdErrorIndices}
                    onItemLinesChange={(updatedItemLines) => setValues(prev => ({ ...prev, itemLines: updatedItemLines }))}
                    onPdErrorChange={(updatedPdErrorIndices) => setValues(prev => ({ ...prev, pdErrorIndices: updatedPdErrorIndices }))}
                />
            </div>


            <div className="space-y-2 mt-6">
                <div className="flex items-center gap-1">
                    <label htmlFor="prescription_files" className="block text-sm font-medium text-gray-700">Upload Prescription(s)</label>
                    <TooltipElement text="Uploading your prescription is highly recommended to ensure all relevant information is transferred correctly." />
                </div>
                <input
                    type="file"
                    id="prescription_files"
                    name="prescription_files"
                    multiple={true}
                    onChange={handleImageUpload}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 cursor-pointer"
                />
                {/* Display uploaded file names */}
                {files.length > 0 && (
                    <div className="mt-2 text-sm text-gray-600">
                        <p className="font-medium">Uploaded files:</p>
                        <ul>
                            {files.map((file, index) => (
                                <li key={index}>{file.name}</li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>

            <RxDiscountHandler />

            <div className="flex items-center space-x-3 pt-4 border-t border-gray-200 mt-6">
                <input
                    type="checkbox"
                    name="verify_inputs"
                    id="verify_inputs"
                    required
                    className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 cursor-pointer"
                />
                <label htmlFor="verify_inputs" className="text-sm font-medium text-gray-700 cursor-pointer">I verify that all inputs are correct</label>
            </div>

            <button
                type="submit" /* Assuming this button is inside a <form> element higher up */
                className="w-full mt-6 px-6 py-3 bg-orange-600 text-white font-semibold rounded-lg shadow-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition duration-150 ease-in-out text-base disabled:opacity-50"
                disabled={!changesMade || values.itemLines.length === 0 || loading}
            >
                Proceed
            </button>
        </>
    );
};


